/**
 * LeadAgentWorkspace — documents + team notes section of the client workspace.
 *   1. Documents & Plans (orderly, project-filterable — no vanity KPI counts)
 *   2. Team Notes strip (collapsible, directly below the documents)
 * The bidding/marketing agent panels open in a modal, triggered from the
 * workspace sidebar via OPEN_AGENT_EVENT.
 * Relies on tokens from pm-shared.css (--pm-*).
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import type { Lead, EnhancedDeal, BiddingTimberItem } from '../../types';
import { Calculator, Sparkles, X } from 'lucide-react';
import { getLeadDocuments } from '../../utils/leadDocumentService';
import { getProjectDisplayName } from '../../utils/projectName';
import AgentDocumentsHub, { type DealFilterOption } from './AgentDocumentsHub';
import AgentBiddingPanel from './AgentBiddingPanel';
import AgentMarketingPanel from './AgentMarketingPanel';
import LeadAgentCommentsStrip from './LeadAgentCommentsStrip';
import LeadCommsSection from './LeadCommsSection';
import './LeadAgentWorkspace.css';

/** Dispatch with detail 'bidding' | 'marketing' to open an agent panel. */
export const OPEN_AGENT_EVENT = 'lctf:openAgentPanel';

interface ViewerDoc {
  url: string;
  name: string;
  id?: string;
}

interface LeadAgentWorkspaceProps {
  lead: Lead;
  leadId: string;
  relatedDeals: EnhancedDeal[];
  activeDealId?: string | null;
  onOpenPdf: (doc: ViewerDoc, opts?: { annotationId?: string; pageNumber?: number }) => void;
  onOpenImage?: (doc: ViewerDoc & { mimeType?: string }, opts?: { annotationId?: string }) => void;
  onOpenCsv?: (doc: ViewerDoc & { mimeType?: string }, opts?: { annotationId?: string }) => void;
  /** Comments strip uses the LeadComments viewer callback shape (url-first). */
  onOpenPdfFromComment?: (url: string, title: string, documentId: string, options?: { annotationId?: string; pageNumber?: number }) => void;
  onOpenImageFromComment?: (url: string, title: string, documentId: string, options?: { annotationId?: string; mimeType?: string }) => void;
  onOpenCsvFromComment?: (url: string, title: string, documentId: string, options?: { annotationId?: string; mimeType?: string }) => void;
  expandTeamNotes?: boolean;
  highlightCommentId?: string | null;
  onHighlightConsumed?: () => void;
}

type AgentKind = 'bidding' | 'marketing';

const LeadAgentWorkspace: React.FC<LeadAgentWorkspaceProps> = ({
  lead,
  leadId,
  relatedDeals,
  activeDealId,
  onOpenPdf,
  onOpenImage,
  onOpenCsv,
  onOpenPdfFromComment,
  onOpenImageFromComment,
  onOpenCsvFromComment,
  expandTeamNotes = false,
  highlightCommentId = null,
  onHighlightConsumed,
}) => {
  const [activeAgent, setActiveAgent] = useState<AgentKind | null>(null);
  const [docIdToUrl, setDocIdToUrl] = useState<Map<string, string>>(new Map());
  const [teamCommentCount, setTeamCommentCount] = useState(0);
  const [docPinCount, setDocPinCount] = useState(0);

  const documentsRef = useRef<HTMLDivElement>(null);
  const notesRef = useRef<HTMLDivElement>(null);

  const dealOptions: DealFilterOption[] = relatedDeals.map(d => ({
    id: d.id!,
    name: getProjectDisplayName(d) || d.title || d.id!,
  }));

  /* ── Open agent panels from anywhere (workspace sidebar nav) ───────────── */
  useEffect(() => {
    const onOpen = (e: Event) => {
      const kind = (e as CustomEvent).detail;
      if (kind === 'bidding' || kind === 'marketing') setActiveAgent(kind);
    };
    window.addEventListener(OPEN_AGENT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_AGENT_EVENT, onOpen);
  }, []);

  // Resolve agent doc ids → download URLs so "View on plan" doesn't open a raw id.
  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    getLeadDocuments(leadId)
      .then(docs => {
        if (cancelled) return;
        const map = new Map<string, string>();
        docs.forEach(d => {
          if (d.id) map.set(d.id, d.url);
        });
        setDocIdToUrl(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  /* ── Modal: lock background scroll + close on Escape ───────────────────── */
  useEffect(() => {
    if (!activeAgent) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveAgent(null);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [activeAgent]);

  const handleViewOnPlan = useCallback((item: BiddingTimberItem) => {
    if (!item.documentId) return;
    setActiveAgent(null);
    const isHttp = /^https?:\/\//i.test(item.documentId);
    const url = isHttp ? item.documentId : (docIdToUrl.get(item.documentId) || item.documentId);
    onOpenPdf(
      { url, name: item.name || 'Plan', id: item.documentId },
      { annotationId: item.annotationId, pageNumber: item.pageNumber },
    );
  }, [onOpenPdf, docIdToUrl]);

  return (
    <section className="law-root" data-tour="cw-agent-workspace">
      {/* ── Documents & Plans (hero) ── */}
      <div className="law-section" ref={documentsRef} data-tour="cw-documents">
        <AgentDocumentsHub
          leadId={leadId}
          lead={lead}
          deals={dealOptions}
          activeDealId={activeDealId}
          onCommentCountChange={setTeamCommentCount}
          onLeadPinCountChange={setDocPinCount}
          onOpenPdf={onOpenPdf}
          onOpenImage={onOpenImage}
          onOpenCsv={onOpenCsv}
        />
      </div>

      {/* ── Team Notes (directly below documents, collapsible) ── */}
      <div ref={notesRef} data-tour="cw-team-notes">
        <LeadAgentCommentsStrip
          leadId={leadId}
          leadName={`${lead.firstName} ${lead.lastName}`.trim()}
          commentCount={teamCommentCount}
          pinCount={docPinCount}
          onOpenPdf={onOpenPdfFromComment}
          onOpenImage={onOpenImageFromComment}
          onOpenCsv={onOpenCsvFromComment}
          expandOnMount={expandTeamNotes}
          highlightCommentId={highlightCommentId}
          onHighlightConsumed={onHighlightConsumed}
        />
      </div>

      {/* ── Comms: calls, meetings, emails, notes (agent + staff) ── */}
      <div data-tour="cw-comms">
        <LeadCommsSection leadId={leadId} />
      </div>

      {/* ── Agent modal (opened from the Bidding/Marketing KPI cards) ── */}
      {activeAgent && (
        <div
          className="law-agent-modal-backdrop"
          onClick={() => setActiveAgent(null)}
          role="dialog"
          aria-modal="true"
          aria-label={activeAgent === 'bidding' ? 'Bidding agent' : 'Marketing agent'}
        >
          <div className="law-agent-modal" onClick={e => e.stopPropagation()}>
            <div className="law-agent-modal-header">
              <span className="law-agent-modal-title">
                {activeAgent === 'bidding' ? (
                  <><Calculator size={15} /> Bidding Agent</>
                ) : (
                  <><Sparkles size={15} /> Marketing Agent</>
                )}
              </span>
              <button
                type="button"
                className="law-agent-modal-close"
                onClick={() => setActiveAgent(null)}
                title="Close (Esc)"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
            <div className="law-agent-modal-body">
              {activeAgent === 'bidding' ? (
                <AgentBiddingPanel lead={lead} deals={relatedDeals} onViewOnPlan={handleViewOnPlan} />
              ) : (
                <AgentMarketingPanel lead={lead} />
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default LeadAgentWorkspace;
